import { ProductCurrencyType } from "@/types/graphql/partner.generated";
import { useCurrentPlatformQuery } from "./useCurrentPlatformQuery";

export interface PaymentProviderAvailability {
  stripeConnected: boolean;
  paynetConnected: boolean;
  ccbillConnected: boolean;
  paypalConnected: boolean;
  /** Валюты, доступные платформе для новых цен — пусто, пока не подключён
   * ни один провайдер. */
  availableCurrencies: ProductCurrencyType[];
  /** Валюты, в которых можно продавать подписку: только провайдеры с
   * продлением (Stripe, CCBill), Paynet и PayPal подписки не продлевают. */
  subscriptionCurrencies: ProductCurrencyType[];
}

/** Копия CURRENCIES_BY_PROVIDER из backend/src/domains/payments/services/
 * ProviderAvailabilityService.ts. Менять оба места вместе. */
const CURRENCIES_BY_PROVIDER = {
  stripe: [
    ProductCurrencyType.EurType,
    ProductCurrencyType.UsdType,
    ProductCurrencyType.GbpType,
    ProductCurrencyType.CadType,
    ProductCurrencyType.PlnType,
  ],
  paynet: [ProductCurrencyType.MdlType, ProductCurrencyType.EurType],
  ccbill: [
    ProductCurrencyType.UsdType,
    ProductCurrencyType.EurType,
    ProductCurrencyType.CadType,
  ],
  paypal: [
    ProductCurrencyType.UsdType,
    ProductCurrencyType.EurType,
    ProductCurrencyType.GbpType,
    ProductCurrencyType.CadType,
    ProductCurrencyType.PlnType,
  ],
} as const;

/**
 * Зеркалит (только для UX-подсказок формы продукта, НЕ источник авторизации)
 * CURRENCIES_BY_PROVIDER из backend/src/domains/payments/services/
 * ProviderAvailabilityService.ts: Stripe обслуживает EUR/USD/GBP/CAD/PLN,
 * Paynet MDL/EUR, CCBill USD/EUR/CAD, PayPal USD/EUR/GBP/CAD/PLN. Настоящая
 * защита это валидатор (ProductValidator.SUBSCRIPTION_MDL_MESSAGE) и сервис
 * (ProductService.assertPricesCurrenciesAvailable) на бэкенде; эта форма
 * вторая линия, чтобы не завести пользователя в тупик, а не обеспечить
 * безопасность.
 */
export function usePaymentProviderAvailability(): PaymentProviderAvailability {
  const { data } = useCurrentPlatformQuery();
  const settings = data?.currentPlatform?.paymentSettings;

  const stripeConnected = Boolean(settings?.connectedAt);
  const paynetConnected = Boolean(settings?.paynet);
  // Блоки ccbill и paypal приходят из схемы только когда бэкенд посчитал
  // подключение полным (isCCBillConnected, isPayPalConnected), так что
  // достаточно проверить сам объект, ровно как для paynet выше.
  const ccbillConnected = Boolean(settings?.ccbill);
  const paypalConnected = Boolean(settings?.paypal);

  const currencies = new Set<ProductCurrencyType>();
  const add = (list: readonly ProductCurrencyType[]) =>
    list.forEach((currency) => currencies.add(currency));
  if (stripeConnected) add(CURRENCIES_BY_PROVIDER.stripe);
  if (paynetConnected) add(CURRENCIES_BY_PROVIDER.paynet);
  if (ccbillConnected) add(CURRENCIES_BY_PROVIDER.ccbill);
  if (paypalConnected) add(CURRENCIES_BY_PROVIDER.paypal);

  const subscription = new Set<ProductCurrencyType>();
  if (stripeConnected) {
    CURRENCIES_BY_PROVIDER.stripe.forEach((c) => subscription.add(c));
  }
  if (ccbillConnected) {
    CURRENCIES_BY_PROVIDER.ccbill.forEach((c) => subscription.add(c));
  }

  return {
    stripeConnected,
    paynetConnected,
    ccbillConnected,
    paypalConnected,
    availableCurrencies: [...currencies],
    subscriptionCurrencies: [...subscription],
  };
}
